import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AreaModel } from '../area.model';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { IAreaToDeleteData } from './area-to-delete-data.model';
import { AreaDeleteModel } from './area-delete.model';
import { AppTextService } from '@fi-shared/app-text';
import { AreaService } from '../area.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import {
    DialogYesNoInputComponent,
    IDialogYesNoInputData,
} from '@fi-app/utilities/dialog/dialog-yes-no-input/dialog-yes-no-input.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

@Component({
    selector: 'fi-area-delete',
    templateUrl: './area-delete.component.html',
    styleUrls: ['./area-delete.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AreaDeleteComponent implements OnInit {
    @Input() area: AreaModel;

    optionDeleteUsersAndUnits;
    optionUserContent;
    optionAreaContent;

    areaDeleteModel = new AreaDeleteModel();
    areaToDeleteData: IAreaToDeleteData | any;

    constructor(
        private backend: FiBackendService,
        private appTextService: AppTextService,
        private areaService: AreaService,
        private loadingService: LoadingService,
        private objectTypesService: ObjectTypesService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
    ) {}

    ngOnInit() {
        this.areaDeleteModel.AreaIdToDelete = this.area.Id;
        this.backend
            .fetchData<IAreaToDeleteData>('Core/Area/GetAreaToDeleteData/' + this.area.Id)
            .then((data) => {
                this.areaToDeleteData = data;
            })
            .catch(() => {
                this.areaToDeleteData = false;
            });

        this.saveChangesWarningService.start(() => this.areaDeleteModel);
    }

    getTitle() {
        return (
            this.appTextService.get('common.delete') +
            ' ' +
            this.objectTypesService.GetNameByObjectType(ObjectTypes.Area).toLowerCase() +
            ' ' +
            this.area.Name
        );
    }

    optionDeleteUsersAndUnitsChange() {
        this.areaDeleteModel.AreaIdToTransferUnits = undefined;
        this.areaDeleteModel.AreaIdToTransferContent = undefined;
        this.areaDeleteModel.OwnerOfSharedItemsIds =
            this.areaToDeleteData && this.areaToDeleteData.SharedItemsToChangeOwner.length > 0 ? undefined : {};
        this.areaDeleteModel.OwnerOfNotSharedItemsIds =
            this.areaToDeleteData && this.areaToDeleteData.UnsharedItemsToChangeOwner.length > 0 ? undefined : {};
        this.optionAreaContent = undefined;
        this.optionUserContent = undefined;
    }

    isShowTransferUnitsAndUnitsCard() {
        return this.optionDeleteUsersAndUnits === 'transfer';
    }

    isShowTransferSharedContentCard() {
        return (
            this.optionDeleteUsersAndUnits === 'delete' &&
            this.areaToDeleteData &&
            this.areaToDeleteData.SharedItemsToChangeOwner.length > 0
        );
    }

    isTransferUnitsAndUnitsCardCompleted() {
        return (
            (this.optionDeleteUsersAndUnits === 'transfer' && !!this.areaDeleteModel.AreaIdToTransferUnits) ||
            (this.optionDeleteUsersAndUnits === 'delete' && !!this.areaDeleteModel.OwnerOfSharedItemsIds)
        );
    }

    isShowUnsharedUserContentOptionsCard() {
        return (
            this.isTransferUnitsAndUnitsCardCompleted() &&
            this.areaToDeleteData.UnsharedItemsToChangeOwner.length > 0 &&
            this.optionDeleteUsersAndUnits === 'delete'
        );
    }

    optionUnsharedUserContentOnChange() {
        this.optionAreaContent = undefined;
        this.areaDeleteModel.OwnerOfNotSharedItemsIds = undefined;
    }

    isUnsharedUserContentOptionsCardCompleted() {
        return (
            this.areaToDeleteData.UnsharedItemsToChangeOwner.length === 0 ||
            this.optionUserContent === 'delete' ||
            (this.optionUserContent === 'transfer' && !!this.areaDeleteModel.OwnerOfNotSharedItemsIds) ||
            this.optionDeleteUsersAndUnits === 'transfer'
        );
    }

    isShowDeleteUserContentTransferCard() {
        return this.optionUserContent === 'transfer';
    }

    isShowDeleteAreaContentOptionsCard() {
        return (
            this.isTransferUnitsAndUnitsCardCompleted() &&
            this.isUnsharedUserContentOptionsCardCompleted() &&
            this.areaToDeleteData.ItemsToDeleteByAreaRights.length > 0
        );
    }

    isShowDeleteAreaTransferCard() {
        return this.optionAreaContent === 'transfer';
    }

    optionAreaContentOnChange() {
        this.areaDeleteModel.AreaIdToTransferContent = undefined;
    }

    isDeleteAreaContentCardCompleted() {
        return (
            this.areaToDeleteData.ItemsToDeleteByAreaRights.length === 0 ||
            this.optionAreaContent === 'delete' ||
            (this.optionAreaContent === 'transfer' && !!this.areaDeleteModel.AreaIdToTransferContent)
        );
    }

    isShowConfirmationCard() {
        return (
            this.isTransferUnitsAndUnitsCardCompleted() &&
            this.isUnsharedUserContentOptionsCardCompleted() &&
            this.isDeleteAreaContentCardCompleted()
        );
    }

    onConfirmClick() {
        const compareValue = this.appTextService.get('common.verify').toUpperCase();

        const dialogConfirmData: IDialogYesNoInputData = {
            message: this.appTextService.get('area_delete.confirm_dialog_text', {
                '{confirm_word}': compareValue,
            }),
            buttonText: this.appTextService.get('common.confirm'),
            confirmTextToInput: compareValue,
        };

        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('common.warning'),
                contentComponentRef: DialogYesNoInputComponent,
                contentComponentData: dialogConfirmData,
                config: {
                    maxWidth: '400px',
                },
            })
            .onYesButtonClick(() => {
                this.areaService.DeleteArea(this.areaDeleteModel).then(() => {
                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                        ObjectTypes.Area,
                        [this.areaDeleteModel.AreaIdToDelete],
                        this.area.Name,
                    );
                    this.saveChangesWarningService.stop();
                    this.stateService.go(FiStateNames.Area.List);
                });
            });
    }
}
